import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export interface SampleDataStatus {
  present: boolean;
  batchId?: string;
  createdAt?: string;
}

/** Current sample-data state for a workspace ({ present, batchId? }). */
export function useSampleDataStatus(wsId: string | undefined) {
  return useQuery<SampleDataStatus>({
    queryKey: ['sample-data', wsId],
    enabled: !!wsId,
    queryFn: async () =>
      (await api.get<SampleDataStatus>(`/workspaces/${wsId}/sample-data/status`)).data,
  });
}

/**
 * Every query the demo dataset lights up. After load/remove we invalidate all
 * of these so the whole UI (home signals, AP/AR, vendors, inbox counts,
 * budgets) reflects the change immediately rather than on the next poll.
 */
function invalidateSeeded(
  qc: ReturnType<typeof useQueryClient>,
  wsId: string | undefined,
): void {
  if (!wsId) return;
  for (const key of [
    'sample-data',
    'signals',
    'signals-summary',
    'ap-invoices',
    'inbox-counts',
    'ar-invoices',
    'vendors',
    'budgets',
    'budget-utilization',
    'approvals',
    'expense-reports',
  ]) {
    qc.invalidateQueries({ queryKey: [key, wsId] });
  }
}

/** Load (seed) the sample dataset; idempotent server-side. */
export function useLoadSampleData(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await api.post<SampleDataStatus>(`/workspaces/${wsId}/sample-data`)).data,
    onSuccess: () => invalidateSeeded(qc, wsId),
  });
}

/** Remove the sample dataset (never touches real data). */
export function useRemoveSampleData(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await api.post<{ removed: boolean }>(`/workspaces/${wsId}/sample-data/remove`)).data,
    onSuccess: () => invalidateSeeded(qc, wsId),
  });
}
